import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FlowDailyAreaChart, FlowMixDonutChart } from "./AnalyticsChartKit";
import Loader from "../../partner/components/Loader";
import { FLOW_FILTERS, flowTypeLabel } from "../../partner/utils/partnerFlowAnalyticsApi";
import "../../partner/pages/PartnerCheckinAnalytics.css";
import { EmberAnalyticsShell } from "./EmberAnalyticsShell";
import HqFranchiseFilterField from "./HqFranchiseFilterField";

const todayIso = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const MetricCard = ({ label, value, hint }) => (
  <article className="pca-metric">
    <p className="pca-metric__label">{label}</p>
    <p className="pca-metric__value">{value}</p>
    {hint ? <p className="pca-metric__hint">{hint}</p> : null}
  </article>
);

const RankedSimple = ({ items, empty }) => {
  if (!items?.length) {
    return <p className="pca-empty">{empty || "No data in this range."}</p>;
  }
  return (
    <ul className="pca-rank-list">
      {items.map((item) => (
        <li key={item.name} className="pca-rank-list__item">
          <span>{item.name}</span>
          <strong>{formatNumber(item.count ?? item.frequency)}</strong>
        </li>
      ))}
    </ul>
  );
};

/**
 * @param {{
 *   portalLabel: string,
 *   title: string,
 *   subtitle?: string,
 *   portalBase: string,
 *   fetchFlowAnalytics: (p: object) => Promise<object>,
 *   lockedFlowType?: string,
 *   hideFlowChips?: boolean,
 * }} props
 */
export default function FlowAnalyticsView({
  portalLabel,
  title,
  subtitle,
  portalBase,
  fetchFlowAnalytics,
  lockedFlowType,
  hideFlowChips = false,
  hqApi = null,
}) {
  const initialFlow = lockedFlowType || "all";
  const [franchiseId, setFranchiseId] = useState("");
  const [fromDate, setFromDate] = useState(() => daysAgoIso(29));
  const [toDate, setToDate] = useState(() => todayIso());
  const [flowType, setFlowType] = useState(initialFlow);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const effectiveFlow = lockedFlowType || flowType;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchFlowAnalytics({
        fromDate,
        toDate,
        flowType: effectiveFlow,
        franchiseId: franchiseId || undefined,
      });
      setAnalytics(data);
    } catch (err) {
      setError(err?.message || "Failed to load flow analytics");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, effectiveFlow, franchiseId, fetchFlowAnalytics]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = analytics?.summary || {};
  const funnelSteps = Array.isArray(analytics?.funnelSteps) ? analytics.funnelSteps : [];
  const funnelMax = Math.max(...funnelSteps.map((s) => Number(s.value || 0)), 1);
  const daily = useMemo(
    () => (Array.isArray(analytics?.daily) ? analytics.daily : []),
    [analytics],
  );
  const byFlow = useMemo(
    () => (Array.isArray(analytics?.byFlow) ? analytics.byFlow : []),
    [analytics],
  );
  const showAnalysisRecs =
    effectiveFlow === "all" || effectiveFlow === "skin_analysis" || effectiveFlow === "hair_analysis";
  const showLabels =
    effectiveFlow === "all" ||
    effectiveFlow === "hairstyle" ||
    effectiveFlow === "hair_coloring";

  if (loading && !analytics) return <Loader />;

  const filterSlot = (
    <>
      <div className="pca-filters">
        {hqApi ? (
          <HqFranchiseFilterField api={hqApi} value={franchiseId} onChange={setFranchiseId} />
        ) : null}
        <label className="pca-filters__field">
          From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="pca-filters__field">
          To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="pca-btn" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>
      {!hideFlowChips && !lockedFlowType ? (
        <div className="pca-chip-row">
          {FLOW_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`dashboard-filter-chip px-3 py-1.5 text-xs font-medium${
                flowType === f.id ? " is-active" : ""
              }`}
              onClick={() => setFlowType(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );

  const actions = (
    <>
      <Link to={`${portalBase}/staff-report`} className="pca-btn pca-btn--ghost">
        Staff analytics
      </Link>
      <Link to={`${portalBase}/overview`} className="pca-btn pca-btn--ghost">
        ← Overview
      </Link>
    </>
  );

  return (
    <EmberAnalyticsShell
      eyebrow={portalLabel}
      title={title}
      lede={
        subtitle ||
        "Skin and hair analysis, hairstyle, and coloring — access and completions (metadata only)."
      }
      actions={actions}
      filters={filterSlot}
      error={error}
    >
      <div className="pca-metrics pca-metrics--primary">
        <MetricCard label="Accesses" value={formatNumber(summary.accessed)} hint="Session / flow opens" />
        <MetricCard
          label="Analysis complete"
          value={formatNumber(summary.analysisCompleted ?? summary.completed)}
          hint="Skin / hair analysis saved"
        />
        <MetricCard label="Recommendations" value={formatNumber(summary.recommended)} hint="Products or services recommended" />
        <MetricCard label="Purchased / taken" value={formatNumber(summary.purchased)} hint="Checkout or service taken" />
        <MetricCard label="Failed jobs" value={formatNumber(summary.failed)} />
        <MetricCard label="Unique guests" value={formatNumber(summary.uniqueGuests)} />
      </div>

      {showAnalysisRecs && funnelSteps.length ? (
        <section className="pca-panel">
          <h2 className="pca-panel__title">Conversion funnel</h2>
          <ul className="pca-rank-list">
            {funnelSteps.map((step) => {
              const width = Math.max(8, Math.round((Number(step.value || 0) / funnelMax) * 100));
              return (
                <li key={step.id} className="pca-rank-list__item" style={{ display: "block", marginBottom: "0.65rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                    <span>{step.label}</span>
                    <strong>{formatNumber(step.value)}</strong>
                  </div>
                  <div
                    style={{
                      height: "6px",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${width}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, #f97316, #fb923c)",
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <div className="pca-grid pca-grid--charts">
        <section className="pca-panel pca-panel--chart">
          <h2 className="pca-panel__title">Daily activity</h2>
          {daily.length ? (
            <FlowDailyAreaChart data={daily} />
          ) : (
            <p className="pca-panel__empty">No daily events in this range.</p>
          )}
        </section>

        <section className="pca-panel pca-panel--chart">
          <h2 className="pca-panel__title">By flow</h2>
          {byFlow.length ? (
            <FlowMixDonutChart rows={byFlow} labelFn={flowTypeLabel} />
          ) : (
            <p className="pca-panel__empty">No flow events yet.</p>
          )}
        </section>
      </div>

      <div className="pca-grid pca-grid--charts">
        {showLabels ? (
          <section className="pca-panel">
            <h2 className="pca-panel__title">Top looks / styles / colors</h2>
            <RankedSimple items={analytics?.topLabels || []} empty="No look/style/color labels yet." />
          </section>
        ) : null}
        {showAnalysisRecs ? (
          <>
            <section className="pca-panel">
              <h2 className="pca-panel__title">
                Top services
                {effectiveFlow === "hair_analysis" ? " (hair)" : effectiveFlow === "skin_analysis" ? " (skin)" : ""}
              </h2>
              <RankedSimple items={analytics?.topTreatments || []} empty="No service recommendations." />
            </section>
            <section className="pca-panel">
              <h2 className="pca-panel__title">
                Top products
                {effectiveFlow === "hair_analysis" ? " (hair)" : effectiveFlow === "skin_analysis" ? " (skin)" : ""}
              </h2>
              <RankedSimple items={analytics?.topProducts || []} empty="No product recommendations." />
            </section>
          </>
        ) : null}
      </div>
    </EmberAnalyticsShell>
  );
}
